import {
  ProjectCard,
  type ProjectCardProps,
  ProjectListCard,
} from '@aecom-ai-engineering/react-components';
import { Project } from '@consigli/types';
import { type ComponentType, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Route } from '@/routes';
import { formatRelativeTime } from '@/util/date';

import { constructionTypeImage } from './construction-type-image';

/**
 * `ProjectCardProps` and `ProjectListCardProps` are structurally identical —
 * both are `ProjectCardBaseProps` plus the element's own attributes — so one
 * wrapper serves both views with no per-view prop mapping.
 */
type ProjectCardComponent = typeof ProjectCard | typeof ProjectListCard;

interface ProjectCardLinkProps {
  project: Project;
  /** Grid uses `ProjectCard`, list uses `ProjectListCard`. */
  card?: ProjectCardComponent;
  /** The image's rendered width for `sizes`, which only the grid knows. */
  imageSizes?: string;
}

/**
 * A project card that navigates. The `Link` wrapper is the documented
 * composition — ProjectCard.css styles it via a `:focus-visible` ancestor arm, so
 * the focus treatment shows on tab rather than on click.
 *
 * `aria-label` rather than name-from-content: the card is an `<article
 * aria-labelledby>`, and whether a link's name follows that varies by engine.
 * `imageAlt` stays empty so the illustration adds nothing to it.
 *
 * Both views come through here so the accessible name cannot drift between
 * them — a difference only a screen-reader user would ever notice.
 */
export const ProjectCardLink: FC<ProjectCardLinkProps> = ({ project, card, imageSizes }) => {
  const { i18n, t } = useTranslation();
  // Typed from the package rather than a hand-written shape: an inline literal
  // would omit people, maxAvatars and imageAlt, and would not follow
  // ProjectCardBaseProps when it changes — the drift this wrapper exists to
  // make impossible.
  const Card: ComponentType<ProjectCardProps> = card ?? ProjectCard;
  const image = constructionTypeImage(project.constructionTypes);

  // The design replaced the address with an activity line. `updatedAt` is
  // required by projectSchema, so unlike lastUploadedPackage there is no empty
  // case to design around — but it moves on any save of the project row, so the
  // wording is "updated" rather than "edited".
  const updated = t('project-list.updated', {
    when: formatRelativeTime(project.updatedAt, i18n.language),
  });

  // Name and location both, per review. The address is no longer on the card, so
  // this is now the only place it appears — it still identifies a project better
  // than a name two others might share. The update time stays out: useful to
  // read, noise to hear when tabbing a grid of them.
  const label = project.address ? `${project.name}, ${project.address}` : project.name;

  return (
    <Link
      aria-label={label}
      className="h-full"
      // The same name in both views is what lets a card morph from its grid
      // cell to its list row when the view switches. See use-project-view.ts.
      style={{
        viewTransitionName: `project-card-${project.id}`,
        viewTransitionClass: 'project-card',
      }}
      title={label}
      to={`../${Route.PROJECTS}/${project.id}/${Route.AGENTS}`}
    >
      <Card
        className="h-full"
        imageSizes={imageSizes}
        imageSrc={image.src}
        imageSrcSet={image.srcSet}
        subtitle={updated}
        title={project.name}
        titleLevel={2}
      />
    </Link>
  );
};
